// bluaIQ — PWA Install + Offline + WiFi Sync
// Photos stay on phone → upload only on WiFi → $0 data cost

import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface SyncStatus {
  type: string
  status: 'uploading' | 'paused' | 'done' | 'wifi_detected'
  message: string
  total?: number
  uploaded?: number
}

// ─── Network detection ──────────────────────────

function getConnectionType(): 'wifi' | 'cellular' | 'unknown' {
  const conn = (navigator as any).connection
  if (conn) {
    if (conn.type === 'wifi' || conn.type === 'ethernet') return 'wifi'
    if (conn.type === 'cellular') return 'cellular'
    // Some browsers only expose effectiveType (4g, 3g, etc.)
    // but not whether it's WiFi — assume WiFi if saveData is off
    if (conn.saveData === false && conn.downlink > 5) return 'wifi'
    if (conn.type) return conn.type === 'wifi' ? 'wifi' : 'cellular'
  }
  return 'unknown'
}

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [connectionType, setConnectionType] = useState(getConnectionType)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    const handleConnectionChange = () => {
      setConnectionType(getConnectionType())
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    const conn = (navigator as any).connection
    if (conn) {
      conn.addEventListener('change', handleConnectionChange)
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      if (conn) conn.removeEventListener('change', handleConnectionChange)
    }
  }, [])

  return { isOnline, connectionType, isWiFi: connectionType === 'wifi' || connectionType === 'unknown' }
}

// ─── PWA Install + Status Bars ──────────────────

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showBanner, setShowBanner] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null)
  const { isOnline, connectionType } = useNetworkStatus()
  const [pendingCount, setPendingCount] = useState(0)
  // Phase C — bilingual user-facing strings. The component reads the
  // current language from i18next (the same source the rest of the
  // app uses) and renders EN/ES variants via inline ternaries. No
  // locale JSON edits — the strings remain inline because all sites
  // appear once each in a single component. The localStorage key,
  // beforeinstallprompt logic, dismiss-window cooldown, and offline
  // sync behavior are all unchanged.
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
    }

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setTimeout(() => setShowBanner(true), 30000)
    }

    // Listen for sync status from service worker
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SYNC_STATUS') {
        setSyncStatus(event.data)
        // Auto-hide "done" message after 5 seconds
        if (event.data.status === 'done') {
          setTimeout(() => setSyncStatus(null), 5000)
          // Refresh pending count
          updatePendingCount()
        }
      }
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall)
    navigator.serviceWorker?.addEventListener('message', handleSWMessage)
    updatePendingCount()

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage)
    }
  }, [])

  // Update count of pending local photos
  async function updatePendingCount() {
    try {
      const db = await openOfflineDB()
      const count = await db.count('pending-photos')
      setPendingCount(count)
    } catch { /* DB not ready */ }
  }

  // Poll pending count every 30 seconds
  useEffect(() => {
    const interval = setInterval(updatePendingCount, 30000)
    return () => clearInterval(interval)
  }, [])

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') setIsInstalled(true)
    setDeferredPrompt(null)
    setShowBanner(false)
  }, [deferredPrompt])

  const handleDismiss = () => {
    setShowBanner(false)
    localStorage.setItem('pwa-dismissed', Date.now().toString())
  }

  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-dismissed')
    if (dismissed) {
      const daysSince = (Date.now() - parseInt(dismissed)) / (1000 * 60 * 60 * 24)
      if (daysSince < 7) setShowBanner(false)
    }
  }, [])

  return (
    <>
      {/* Offline bar */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-center py-1 px-4 text-sm z-50 flex items-center justify-center gap-2">
          <span>{isEs ? 'Sin conexión — Los datos se sincronizarán automáticamente' : 'Offline — data will sync automatically'}</span>
        </div>
      )}

      {/* Cellular data warning + pending count */}
      {isOnline && connectionType === 'cellular' && pendingCount > 0 && (
        <div className="fixed top-0 left-0 right-0 bg-[#06539B] text-white text-center py-1 px-4 text-sm z-50 flex items-center justify-center gap-2">
          <span>
            {isEs
              ? `${pendingCount} foto(s) guardadas. Se subirán cuando conectes a WiFi.`
              : `${pendingCount} photo(s) saved. They will upload when you connect to WiFi.`}
          </span>
        </div>
      )}

      {/* Sync status bar */}
      {syncStatus && (
        <div className={`fixed top-0 left-0 right-0 text-white text-center py-1 px-4 text-sm z-50 flex items-center justify-center gap-2 ${
          syncStatus.status === 'uploading' ? 'bg-[#06539B]' :
          syncStatus.status === 'paused' ? 'bg-amber-500' :
          syncStatus.status === 'wifi_detected' ? 'bg-[#06539B]' :
          'bg-green-600'
        }`}>
          {syncStatus.status === 'uploading' && (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          <span>{syncStatus.message}</span>
          {syncStatus.total && syncStatus.uploaded !== undefined && (
            <div className="w-20 h-1.5 bg-white/30 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all"
                style={{ width: `${(syncStatus.uploaded / syncStatus.total) * 100}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Install banner */}
      {showBanner && !isInstalled && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#06539B] border-t border-[#14457E] p-4 z-50 safe-bottom">
          <div className="max-w-lg mx-auto flex items-center gap-4">
            <div className="rounded-xl p-1 flex-shrink-0">
              <img src="/natami_icon.png" alt="NATAMI" className="w-10 h-10 rounded-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">{isEs ? 'Instala NATAMI' : 'Install NATAMI'}</p>
              <p className="text-slate-400 text-xs">
                {isEs
                  ? 'Toma fotos en obra, se suben solas por WiFi. $0 de datos.'
                  : 'Take jobsite photos and upload them automatically over WiFi. $0 mobile data.'}
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={handleDismiss} className="text-slate-400 text-sm px-3 py-1.5 hover:text-white">{isEs ? 'Luego' : 'Later'}</button>
              <button onClick={handleInstall} className="bg-[#06539B] text-white text-sm font-medium px-4 py-1.5 rounded-lg hover:bg-[#14457E]">{isEs ? 'Instalar' : 'Install'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── Smart Upload Hook: WiFi-only for photos ────

export function useSmartUpload() {
  const { isOnline, isWiFi } = useNetworkStatus()
  // Phase D — bilingual return messages. The hook is consumed from
  // PhotosModule / ReceiptsModule and the returned `message` string
  // is rendered directly to the user (toast / status banner). Before
  // Phase D the messages were Spanish-only, so English users saw
  // Spanish text. We read the current language from i18next via the
  // same hook the rest of the app uses; no behavioral change to the
  // offline / WiFi / sync logic, only the rendered text.
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'

  // Take photo and save locally — upload only on WiFi
  const savePhoto = async (
    projectId: string,
    file: File,
    options: { phase?: string; description?: string; lifecycleTaskId?: string } = {}
  ): Promise<{ queued: boolean; localId?: number; message: string }> => {

    const db = await openOfflineDB()

    // Always save locally first (instant, no data cost)
    const thumbnail = await createThumbnail(file, 200)
    const localId = await db.add('local-photos', {
      projectId,
      file,
      thumbnail,
      phase: options.phase,
      description: options.description,
      lifecycleTaskId: options.lifecycleTaskId,
      createdAt: new Date(),
      synced: false
    })

    // If on WiFi, also queue for immediate upload
    if (isOnline && isWiFi) {
      await db.add('pending-photos', {
        projectId,
        file,
        phase: options.phase || '',
        description: options.description || '',
        lifecycleTaskId: options.lifecycleTaskId || '',
        localId,
        createdAt: new Date()
      })

      // Trigger sync now
      const reg = await navigator.serviceWorker.ready
      await (reg as any).sync?.register('sync-photos')

      return {
        queued: true,
        localId,
        message: isEs
          ? 'Foto guardada y subiendo por WiFi...'
          : 'Photo saved and uploading over WiFi...',
      }
    }

    // Not on WiFi — save for later
    await db.add('pending-photos', {
      projectId,
      file,
      phase: options.phase || '',
      description: options.description || '',
      lifecycleTaskId: options.lifecycleTaskId || '',
      localId,
      createdAt: new Date()
    })

    return {
      queued: true,
      localId,
      message: isEs
        ? (isOnline
            ? 'Foto guardada. Se subirá cuando conectes a WiFi.'
            : 'Foto guardada. Se subirá cuando tengas WiFi.')
        : (isOnline
            ? 'Photo saved. It will upload when you connect to WiFi.'
            : 'Photo saved. It will upload when you have WiFi.'),
    }
  }

  // Save receipt locally — upload on WiFi (needs AI = bigger payload)
  const saveReceipt = async (
    projectId: string,
    file: File
  ): Promise<{ queued: boolean; message: string }> => {

    const db = await openOfflineDB()

    if (isOnline && isWiFi) {
      await db.add('pending-receipts', { projectId, file, createdAt: new Date() })
      const reg = await navigator.serviceWorker.ready
      await (reg as any).sync?.register('sync-receipts')
      return {
        queued: true,
        message: isEs
          ? 'Recibo guardado y escaneando por WiFi...'
          : 'Receipt saved and scanning over WiFi...',
      }
    }

    await db.add('pending-receipts', { projectId, file, createdAt: new Date() })
    return {
      queued: true,
      message: isEs
        ? (isOnline
            ? 'Recibo guardado. Se escaneará por WiFi (usa AI).'
            : 'Recibo guardado. Se escaneará cuando tengas WiFi.')
        : (isOnline
            ? 'Receipt saved. It will be scanned over WiFi (uses AI).'
            : 'Receipt saved. It will be scanned when you have WiFi.'),
    }
  }

  // Get locally saved photos for a project (viewable offline)
  const getLocalPhotos = async (projectId: string) => {
    const db = await openOfflineDB()
    return db.getAllByIndex('local-photos', 'projectId', projectId)
  }

  // Get pending upload counts
  const getPendingCounts = async () => {
    const db = await openOfflineDB()
    return {
      photos: await db.count('pending-photos'),
      receipts: await db.count('pending-receipts')
    }
  }

  // Force sync now (user tapped "sync" button while on WiFi)
  const forceSyncNow = async () => {
    if (!isOnline) {
      return {
        success: false,
        message: isEs ? 'Sin conexión a internet' : 'No internet connection',
      }
    }

    const reg = await navigator.serviceWorker.ready
    await (reg as any).sync?.register('sync-photos')
    await (reg as any).sync?.register('sync-receipts')
    return {
      success: true,
      message: isEs ? 'Sincronización iniciada...' : 'Sync started...',
    }
  }

  return { savePhoto, saveReceipt, getLocalPhotos, getPendingCounts, forceSyncNow, isWiFi, isOnline }
}

// ─── Create thumbnail for local gallery ─────────

function createThumbnail(file: File, maxSize: number): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ratio = Math.min(maxSize / img.width, maxSize / img.height)
        canvas.width = img.width * ratio
        canvas.height = img.height * ratio
        const ctx = canvas.getContext('2d')!
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', 0.6))
      }
      img.src = e.target?.result as string
    }
    reader.readAsDataURL(file)
  })
}

// ─── IndexedDB helper ───────────────────────────

function openOfflineDB(): Promise<any> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('constructai-offline', 2)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains('pending-photos')) {
        db.createObjectStore('pending-photos', { keyPath: 'id', autoIncrement: true })
      }
      if (!db.objectStoreNames.contains('pending-receipts')) {
        db.createObjectStore('pending-receipts', { keyPath: 'id', autoIncrement: true })
      }
      if (!db.objectStoreNames.contains('local-photos')) {
        const store = db.createObjectStore('local-photos', { keyPath: 'id', autoIncrement: true })
        store.createIndex('projectId', 'projectId')
      }
    }
    req.onsuccess = () => {
      const db = req.result
      resolve({
        add: (store: string, data: any) => new Promise<number>((res) => {
          const tx = db.transaction(store, 'readwrite')
          const addReq = tx.objectStore(store).add(data)
          addReq.onsuccess = () => res(addReq.result as number)
        }),
        getAll: (store: string) => new Promise((res) => {
          const tx = db.transaction(store, 'readonly')
          const all = tx.objectStore(store).getAll()
          all.onsuccess = () => res(all.result)
        }),
        getAllByIndex: (store: string, index: string, value: any) => new Promise((res) => {
          const tx = db.transaction(store, 'readonly')
          const idx = tx.objectStore(store).index(index)
          const all = idx.getAll(value)
          all.onsuccess = () => res(all.result)
        }),
        count: (store: string) => new Promise<number>((res) => {
          const tx = db.transaction(store, 'readonly')
          const c = tx.objectStore(store).count()
          c.onsuccess = () => res(c.result)
        }),
        delete: (store: string, id: number) => new Promise<void>((res) => {
          const tx = db.transaction(store, 'readwrite')
          tx.objectStore(store).delete(id)
          tx.oncomplete = () => res()
        }),
      })
    }
    req.onerror = () => reject(req.error)
  })
}
